import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

// レポートタブの種類を定義
export type ReportTabType = 'supervisor' | 'supervisor-customer' | 'company-trend';

// フィルター条件の型定義
export interface ReportFilterParams {
  startDate: string;
  endDate: string;
  unit?: 'monthly' | 'quarterly' | 'half' | 'yearly'; // 集計単位
}

// 各タブのデータキャッシュの型定義
interface ReportDataCache {
  [key: string]: any; // タブごとのデータキャッシュ
}

// コンテキストの状態の型定義
interface ReportContextState {
  // 現在選択されているタブ
  activeTab: ReportTabType;
  // 各タブごとのフィルター設定
  filterParamsMap: Record<ReportTabType, ReportFilterParams>;
  // 各タブごとのデータキャッシュ
  dataCache: ReportDataCache;
  // レポート表示のトリガーフラグ
  shouldDisplayReport: boolean;
  // レポートが既に表示されたかどうかのフラグ (初期表示制御用)
  reportDisplayed: boolean;
}

// コンテキストのアクション（メソッド）の型定義
interface ReportContextActions {
  // タブを変更する
  setActiveTab: (tab: ReportTabType) => void;
  // フィルター設定を更新する
  updateFilterParams: (params: Partial<ReportFilterParams>) => void;
  // 現在アクティブなタブのフィルターパラメータを取得
  getFilterParams: () => ReportFilterParams;
  // 特定のタブのフィルターパラメータを取得
  getTabFilterParams: (tabType: ReportTabType) => ReportFilterParams;
  // キャッシュにデータを保存する
  cacheReportData: (tabType: ReportTabType | string, data: any) => void;
  // レポート表示をトリガーする
  triggerReportDisplay: () => void;
  // レポートが表示されたことをマークする
  markReportAsDisplayed: () => void;
  // キャッシュの有効性をチェックする
  isCacheValid: (tabType: ReportTabType | string) => boolean;
  // キャッシュからデータを取得する
  getCachedData: (tabType: ReportTabType | string) => any;
}

// 先月の年月を取得する関数
const getLastMonth = (): string => {
  const today = new Date();
  today.setMonth(today.getMonth() - 1);
  return today.toISOString().substring(0, 7); // YYYY-MM形式
};

// 現在の会計年度を計算する関数（3月始まり2月締め）
const getCurrentFiscalYear = (): number => {
  const today = new Date();
  const month = today.getMonth() + 1; // JavaScriptの月は0始まり
  const year = today.getFullYear();
  
  // 1月と2月は前年度の会計年度に属する
  return month <= 2 ? year - 1 : year;
};

// 会計年度の開始月と終了月を計算
const getFiscalYearRange = (startYear: number, endYear: number): { startDate: string, endDate: string } => {
  return {
    startDate: `${startYear}-03`,
    endDate: `${endYear + 1}-02` // 終了年度の翌年2月まで
  };
};

// 各タブのデフォルトフィルター設定
const defaultFilterParamsMap: Record<ReportTabType, ReportFilterParams> = {
  // 担当者別レポート：デフォルトで先月
  'supervisor': {
    startDate: getLastMonth(),
    endDate: getLastMonth(),
    unit: 'monthly',
  },
  // 担当者×顧客別レポート：デフォルトで先月
  'supervisor-customer': {
    startDate: getLastMonth(),
    endDate: getLastMonth(),
    unit: 'monthly',
  },
  // 会社全体トレンドレポート：デフォルトで当期会計年度
  'company-trend': {
    ...getFiscalYearRange(getCurrentFiscalYear(), getCurrentFiscalYear()),
    unit: 'quarterly',
  },
};

// コンテキストオブジェクトの型定義
interface ReportContextType extends ReportContextState, ReportContextActions {}

// コンテキストの作成
const ReportContext = createContext<ReportContextType | undefined>(undefined);

// コンテキストプロバイダーの型定義
interface ReportProviderProps {
  children: ReactNode;
}

// コンテキストプロバイダーコンポーネント
export const ReportProvider: React.FC<ReportProviderProps> = ({ children }) => {
  // 状態の初期化
  const [activeTab, setActiveTab] = useState<ReportTabType>('supervisor');
  const [filterParamsMap, setFilterParamsMap] = useState<Record<ReportTabType, ReportFilterParams>>(defaultFilterParamsMap);
  const [dataCache, setDataCache] = useState<ReportDataCache>({});
  const [shouldDisplayReport, setShouldDisplayReport] = useState<boolean>(false);
  const [reportDisplayed, setReportDisplayed] = useState<boolean>(false);

  // 現在アクティブなタブのフィルターパラメータを取得するメソッド
  const getFilterParams = useCallback(() => {
    return filterParamsMap[activeTab];
  }, [activeTab, filterParamsMap]);

  // 特定のタブのフィルターパラメータを取得するメソッド
  const getTabFilterParams = useCallback((tabType: ReportTabType) => {
    return filterParamsMap[tabType];
  }, [filterParamsMap]);

  // フィルター設定を更新するメソッド（現在アクティブなタブのみ）
  const updateFilterParams = useCallback((params: Partial<ReportFilterParams>) => {
    setFilterParamsMap(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], ...params }
    }));
  }, [activeTab]);

  // データをキャッシュに保存するメソッド
  const cacheReportData = useCallback((tabType: ReportTabType | string, data: any) => {
    setDataCache(prev => ({
      ...prev,
      // キャッシュキーはタブタイプとそのタブのフィルター条件の組み合わせ
      [`${tabType}_${JSON.stringify(filterParamsMap[tabType as ReportTabType])}`]: {
        data,
        timestamp: new Date().getTime(),
      },
    }));
  }, [filterParamsMap]);

  // レポート表示をトリガーするメソッド
  const triggerReportDisplay = useCallback(() => {
    setShouldDisplayReport(true);
    setReportDisplayed(true);
  }, []);

  // レポートが表示されたことをマークするメソッド
  const markReportAsDisplayed = useCallback(() => {
    setShouldDisplayReport(false);
  }, []);

  // キャッシュの有効性をチェックするメソッド
  const isCacheValid = useCallback((tabType: ReportTabType | string) => {
    const tabFilterParams = filterParamsMap[tabType as ReportTabType];
    const cacheKey = `${tabType}_${JSON.stringify(tabFilterParams)}`;
    const cachedItem = dataCache[cacheKey];
    
    if (!cachedItem) return false;
    
    // キャッシュの有効期限をチェック（例: 15分）
    const now = new Date().getTime();
    const cacheTime = 15 * 60 * 1000; // 15分
    return (now - cachedItem.timestamp) < cacheTime;
  }, [dataCache, filterParamsMap]);

  // キャッシュからデータを取得するメソッド
  const getCachedData = useCallback((tabType: ReportTabType | string) => {
    const tabFilterParams = filterParamsMap[tabType as ReportTabType];
    const cacheKey = `${tabType}_${JSON.stringify(tabFilterParams)}`;
    const cachedItem = dataCache[cacheKey];
    return cachedItem?.data;
  }, [dataCache, filterParamsMap]);

  // コンテキスト値の作成
  const contextValue: ReportContextType = {
    // 状態
    activeTab,
    filterParamsMap,
    dataCache,
    shouldDisplayReport,
    reportDisplayed,
    // アクション
    setActiveTab,
    updateFilterParams,
    getFilterParams,
    getTabFilterParams,
    cacheReportData,
    triggerReportDisplay,
    markReportAsDisplayed,
    isCacheValid,
    getCachedData,
  };

  return (
    <ReportContext.Provider value={contextValue}>
      {children}
    </ReportContext.Provider>
  );
};

// カスタムフック
export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (context === undefined) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return context;
};

// APIパラメータ変換ヘルパー
export const createReportParams = (filterState: ReportFilterParams) => {
  return {
    startMonth: filterState.startDate,
    endMonth: filterState.endDate,
    unit: filterState.unit
  };
};